import { ReactNode, useRef, MouseEvent } from "react";
import { cn } from "@/lib/utils";

interface ClickRippleProps {
  children: ReactNode;
  className?: string;
  color?: string;
}

/**
 * Wraps children in a relative container and emits a soft cyan ripple
 * from the click point on every pointerdown.
 */
export function ClickRipple({
  children,
  className,
  color = "hsl(var(--primary-glow) / 0.45)",
}: ClickRippleProps) {
  const ref = useRef<HTMLSpanElement>(null);

  const handleClick = (e: MouseEvent<HTMLSpanElement>) => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const host = ref.current;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const size = Math.max(rect.width, rect.height) * 1.5;
    const ripple = document.createElement("span");
    ripple.setAttribute("aria-hidden", "true");
    ripple.style.cssText = `
      position:absolute;left:${x - size / 2}px;top:${y - size / 2}px;
      width:${size}px;height:${size}px;border-radius:9999px;
      background:radial-gradient(circle, ${color} 0%, transparent 60%);
      pointer-events:none;opacity:0.85;transform:scale(0);
      animation:ripple-out 520ms cubic-bezier(0.22,1,0.36,1) forwards;
      z-index:0;
    `;
    host.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);
  };

  return (
    <span
      ref={ref}
      onPointerDown={handleClick}
      className={cn("relative inline-block overflow-hidden", className)}
    >
      {children}
    </span>
  );
}
